import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuth } from '../app/YowanAuthContext';
import { useYowanAuthGuard } from '../app/yowanAuthGuards';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const STAGING_DOMAIN_FALLBACK_HREF = '/admin/deploy/staging-domain-fallback';
const STAGING_SWITCH_DEVELOPER_EMAIL = 'shimazaki@shimashou.com';

const canUseStagingSwitch = (currentUser) =>
  String(currentUser?.email || '').toLowerCase() === STAGING_SWITCH_DEVELOPER_EMAIL;

const filterStagingSwitchNavGroups = (groups, canShowStagingSwitch) =>
  groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => canShowStagingSwitch || item.href !== STAGING_DOMAIN_FALLBACK_HREF
      ),
    }))
    .filter((group) => group.items.length > 0);

const filterStagingSwitchLinkGroups = (groups, canShowStagingSwitch) =>
  groups
    .map((group) => ({
      ...group,
      links: group.links.filter(
        (link) => canShowStagingSwitch || link.href !== STAGING_DOMAIN_FALLBACK_HREF
      ),
    }))
    .filter((group) => group.links.length > 0);

const AdminDashboardPage = ({ guardOptions }) => {
  const content = yowanPageContent['/admin'];
  const metadata = getYowanRouteMeta('/admin');
  const location = useLocation();
  const { currentUser } = useYowanAuth();
  const guard = useYowanAuthGuard({
    requiresAuth: true,
    requiresAdmin: true,
    search: location.search,
    ...guardOptions,
  });
  const shellRef = useRef(null);
  const resizingRef = useRef(false);
  const [compactSidebarMode, setCompactSidebarMode] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(304);
  const [sidebarStoredWidth, setSidebarStoredWidth] = useState(304);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const syncSidebarState = (event) => {
      setCompactSidebarMode(event.matches);
      setSidebarHidden(event.matches);
    };

    syncSidebarState(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncSidebarState);
      return () => mediaQuery.removeEventListener('change', syncSidebarState);
    }

    mediaQuery.addListener(syncSidebarState);
    return () => mediaQuery.removeListener(syncSidebarState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (!resizingRef.current || !shellRef.current) {
        return;
      }

      const bounds = shellRef.current.getBoundingClientRect();
      const rawWidth = event.clientX - bounds.left;

      if (rawWidth <= 132) {
        setSidebarHidden(true);
        return;
      }

      const nextWidth = Math.min(Math.max(rawWidth, 168), 420);
      setSidebarWidth(nextWidth);
      setSidebarStoredWidth(nextWidth);
      setSidebarHidden(false);
    };

    const handlePointerUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  if (!content) {
    return null;
  }

  const canShowStagingSwitch = canUseStagingSwitch(currentUser);
  const visibleNavGroups = filterStagingSwitchNavGroups(content.navGroups, canShowStagingSwitch);
  const visibleLinkGroups = filterStagingSwitchLinkGroups(content.linkGroups, canShowStagingSwitch);

  return (
    <Section className="yowan-admin-page">
      <PageContainer>
        <div
          ref={shellRef}
          className={`yowan-admin-shell${sidebarHidden ? ' yowan-admin-shell--sidebar-hidden' : ''}`}
          style={{ '--admin-sidebar-width': `${sidebarWidth}px` }}
        >
          <aside className="yowan-admin-shell__sidebar">
            <div className="yowan-admin-sidebar">
              {!sidebarHidden ? (
                <button
                  type="button"
                  className="yowan-admin-shell__toggle yowan-admin-shell__toggle--close"
                  aria-label="左ナビを隠す"
                  aria-expanded="true"
                  onClick={() => setSidebarHidden(true)}
                >
                  <span aria-hidden="true">‹</span>
                </button>
              ) : null}
              <Heading as="h1" align="left">
                {content.header.title}
              </Heading>
              {visibleNavGroups.map((group) => (
                <section key={group.title} className="yowan-admin-sidebar__group">
                  <h2 className="yowan-admin-sidebar__group-title">{group.title}</h2>
                  <ul className="yowan-admin-sidebar__list">
                    {group.items.map((item) => (
                      <li key={item.label}>
                        <Link to={appendYowanPreviewQueryIfNeeded(item.href, location.search)}>
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </aside>

          {sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__toggle yowan-admin-shell__toggle--open"
              aria-label="左ナビを表示"
              aria-expanded="false"
              onClick={() => {
                setSidebarWidth(Math.max(sidebarStoredWidth, 224));
                setSidebarHidden(false);
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          ) : null}

          {!sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__resizer"
              aria-label="左ナビの幅を調整"
              onPointerDown={(event) => {
                event.preventDefault();
                resizingRef.current = true;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
            />
          ) : null}

          <div className="yowan-admin-shell__main">
            <div
              className="yowan-admin-shell__content"
              onClick={() => {
                if (compactSidebarMode && !sidebarHidden) {
                  setSidebarHidden(true);
                }
              }}
            >
              <div className="yowan-admin-overview">
                {content.header.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="yowan-admin-overview__lead">
                    {paragraph}
                  </p>
                ))}
                {guard.status === 'login-required' ? (
                  <p className="yowan-admin-overview__lead">
                    {guard.notice.message}
                    <br />
                    <Link to={guard.notice.actionHref}>{guard.notice.actionLabel}</Link>
                  </p>
                ) : null}
                {guard.status === 'admin-required' ? (
                  <p className="yowan-admin-overview__lead">
                    <LoadingSpinner size="sm" />
                    <span>管理権限を確認中</span>
                  </p>
                ) : null}
              </div>

              <div className="yowan-admin-link-groups">
                {visibleLinkGroups.map((group) => (
                  <section key={group.title} className="yowan-admin-link-groups__section">
                    <Heading as="h2">{group.title}</Heading>
                    <p className="yowan-admin-link-groups__lead">{group.body}</p>
                    <ul className="yowan-admin-link-groups__list">
                      {group.links.map((link) => (
                        <li key={link.label}>
                          <Link to={appendYowanPreviewQueryIfNeeded(link.href, location.search)}>
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </Section>
  );
};

export default AdminDashboardPage;
